// src/pages/GeneralPayments.jsx
import React, {
  useMemo,
  useState,
  useEffect,
  useRef,
  useCallback,
} from "react";
import { motion, AnimatePresence } from "framer-motion";
import Select from "react-select";
import toast from "react-hot-toast";
import { skipToken } from "@reduxjs/toolkit/query";
import InvoicePrintPreviewModal from "../components/InvoicePrintPreviewModal";
import { FaRedoAlt, FaUserCircle, FaWallet } from "react-icons/fa";
import { FiPrinter, FiRotateCcw } from "react-icons/fi";

import { useGetAllCustomersQuery } from "../toolkitServices/customerApi";
import {
  useGetCustomerTransactionsQuery,
  useReverseCustomerTransactionMutation,
} from "../toolkitServices/customerTransactionApi";
import WalletPayWithdrawModal from "../components/Customer/WalletPayWithdrawModal";

/* ---------------- theme tokens ---------------- */
const BRAND = {
  header: "#9481ce",
  primary: "#6b59c6",
  ring: "#7c3aed",
};

/* ---------------- animations ---------------- */
const fadeUp = (delay = 0) => ({
  hidden: { opacity: 0, y: 14 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { delay, duration: 0.45, ease: "easeOut" },
  },
});

const listVariants = {
  hidden: { opacity: 0, y: 8 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.04, duration: 0.35, ease: "easeOut" },
  }),
};

/* ---------------- helpers ---------------- */
const toOpt = (c) => ({
  value: c?._id,
  label: `${c?.customerName || "Customer"}${
    c?.customerId ? ` · ${c.customerId}` : ""
  }`,
  raw: c,
});

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const fmtINR = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return "₹0.00";
  return `₹${n.toFixed(2)}`;
};

const toYmd = (d) => {
  if (!d) return "-";
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return String(d).slice(0, 10);
  return dt.toISOString().slice(0, 10);
};

// ✅ robust ledger id pick (so slip works for every row)
const pickTxLedgerId = (r) =>
  r?.ledgerId ||
  r?.ledger?._id ||
  r?.transactionId ||
  r?.receiptId ||
  r?._id ||
  null;

const isReversalRow = (r) => {
  const st = String(r?.sourceType || "").toUpperCase();
  return !!r?.isReversal || st === "REVERSAL";
};

// ✅ wallet detection (UPDATED)
const isWalletRow = (r) => {
  const st = String(r?.sourceType || "").toUpperCase();
  const dst = String(r?.displaySourceType || "").toUpperCase();
  const ost = String(r?.originalSourceType || "").toUpperCase();
  const tp = String(r?.type || r?.txnType || "").toUpperCase();
  const kd = String(r?.kind || "").toUpperCase();

  return (
    st.includes("WALLET") ||
    dst.includes("WALLET") ||
    ost.includes("WALLET") ||
    tp.includes("WALLET") ||
    kd.includes("WALLET") ||
    st === "CUSTOMER_WALLET" ||
    st === "WALLET_TOPUP" ||
    st === "WALLET_WITHDRAW" ||
    kd === "WALLET_ADD" ||
    kd === "WALLET_WITHDRAW"
  );
};

const getWalletAction = (r) => {
  const kd = String(r?.kind || "").toUpperCase();
  if (kd === "WALLET_WITHDRAW") return "WITHDRAW";
  if (kd === "WALLET_ADD") return "ADD";

  const c = num(r?.credit);
  const d = num(r?.debit);
  if (d > 0 && c <= 0) return "WITHDRAW";
  return "ADD";
};

const pickWalletBalance = (customerRaw) => {
  if (!customerRaw) return null;
  const candidates = [
    customerRaw?.walletBalance,
    customerRaw?.walletAmount,
    customerRaw?.wallet,
    customerRaw?.walletAmt,
    customerRaw?.balanceWallet,
    customerRaw?.wallet_balance,
    customerRaw?.balanceAmount,
  ];
  const v = candidates.find((x) => x != null && x !== "");
  return v == null ? null : num(v);
};

/* ---------------- react-select styling ---------------- */
const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 34,
    height: 34,
    borderRadius: 10,
    borderColor: state.isFocused ? BRAND.ring : "#e5e7eb",
    boxShadow: state.isFocused ? `0 0 0 3px ${BRAND.ring}22` : "none",
    "&:hover": { borderColor: BRAND.ring },
    fontSize: "0.8rem",
    backgroundColor: "white",
  }),
  valueContainer: (base) => ({ ...base, padding: "0 8px" }),
  singleValue: (base) => ({
    ...base,
    maxWidth: "100%",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    color: "#111827",
    fontWeight: 600,
  }),
  placeholder: (base) => ({ ...base, color: "#9ca3af", fontSize: "0.8rem" }),
  input: (base) => ({ ...base, margin: 0, padding: 0, color: "#111827" }),
  indicatorsContainer: (base) => ({ ...base, height: 34 }),
  option: (base, state) => ({
    ...base,
    fontSize: "0.8rem",
    backgroundColor: state.isFocused
      ? "#ede9fe"
      : state.isSelected
        ? "#ddd6fe"
        : "white",
    color: "#111827",
  }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  menu: (base) => ({
    ...base,
    zIndex: 9999,
    borderRadius: 12,
    overflow: "hidden",
  }),
};

const menuPortalTarget = typeof document !== "undefined" ? document.body : null;

/* ---------------- small UI components ---------------- */
const ListLoadingPill = ({ show }) => {
  return (
    <AnimatePresence>
      {show ? (
        <motion.div
          initial={{ opacity: 0, y: -6 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -6 }}
          className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[11px] font-semibold text-slate-600 shadow-sm"
        >
          <span className="relative inline-flex h-4 w-4 items-center justify-center">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-slate-300/60" />
            <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-slate-400" />
          </span>
          Loading transactions…
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
};

const SkeletonTxRow = () => (
  <div className="rounded-2xl border bg-white px-3 py-2.5 shadow-sm">
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <div className="h-5 w-16 rounded-full bg-slate-200 animate-pulse" />
          <div className="h-4 w-44 rounded bg-slate-200 animate-pulse" />
        </div>
        <div className="mt-2 space-y-2">
          <div className="h-3 w-64 rounded bg-slate-200 animate-pulse" />
          <div className="h-3 w-40 rounded bg-slate-200 animate-pulse" />
        </div>
      </div>
      <div className="shrink-0 text-right space-y-2">
        <div className="h-5 w-24 rounded bg-slate-200 animate-pulse ml-auto" />
        <div className="h-3 w-14 rounded bg-slate-200 animate-pulse ml-auto" />
        <div className="h-7 w-16 rounded-full bg-slate-200 animate-pulse ml-auto" />
      </div>
    </div>
  </div>
);

const ListShimmerOverlay = ({ show }) => (
  <AnimatePresence>
    {show ? (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="pointer-events-none absolute inset-0"
      >
        <div className="absolute inset-0 bg-gradient-to-b from-white/65 via-white/40 to-white/65" />
        <div className="absolute inset-x-0 top-0 h-28 bg-gradient-to-r from-transparent via-slate-100/80 to-transparent animate-[shimmer_1.25s_linear_infinite]" />
        <style>{`
          @keyframes shimmer {
            0% { transform: translateX(-100%); }
            100% { transform: translateX(100%); }
          }
        `}</style>
      </motion.div>
    ) : null}
  </AnimatePresence>
);

export default function GeneralPayments() {
  const [customerOpt, setCustomerOpt] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalAction, setModalAction] = useState("ADD"); // ADD | WITHDRAW

  const [receiptPreview, setReceiptPreview] = useState({
    open: false,
    html: "",
    loading: false,
  });

  const [reverseTx, reverseState] = useReverseCustomerTransactionMutation();
  const [reverseOpen, setReverseOpen] = useState(false);
  const [reverseTarget, setReverseTarget] = useState(null);

  // ✅ show all by default
  const [showReversed] = useState(true);

  const customerSelectRef = useRef(null);
  const wasModalOpenRef = useRef(false);

  // ✅ IMPORTANT: when success happens, we close the add/withdraw modal,
  // but we must NOT focus customer immediately because print-confirm will open.
  const skipCustomerFocusOnceRef = useRef(false);

  const customerId = customerOpt?.value || null;

  const {
    data: customerRes,
    isFetching: custLoading,
    refetch: refetchCustomers,
  } = useGetAllCustomersQuery({});

  const customers = useMemo(
    () => customerRes?.data || customerRes?.rows || customerRes || [],
    [customerRes],
  );
  const customerOptions = useMemo(
    () => (Array.isArray(customers) ? customers.map(toOpt) : []),
    [customers],
  );

  // ✅ fresh selectedCustomer so wallet balance updates
  const selectedCustomer = useMemo(() => {
    if (!customerId) return null;
    const list = Array.isArray(customers) ? customers : [];
    const fresh = list.find((c) => String(c?._id) === String(customerId));
    return fresh || customerOpt?.raw || null;
  }, [customers, customerId, customerOpt]);

  const {
    data: txnRes,
    isFetching: txnLoading,
    refetch: refetchTxns,
  } = useGetCustomerTransactionsQuery(
    customerId ? { customerId, page: 1, limit: 200, type: "ALL" } : skipToken,
  );

  const txRows = useMemo(() => {
    const raw = txnRes?.rows || txnRes?.data?.rows || txnRes?.data || [];
    return Array.isArray(raw) ? raw : [];
  }, [txnRes]);

  const walletRows = useMemo(() => {
    const rows = txRows.filter(isWalletRow);
    if (showReversed) return rows;
    return rows.filter((r) => !r?.isReversed && !isReversalRow(r));
  }, [txRows, showReversed]);

  // ✅ focus customer select when add/withdraw modal closes (BUT skip if success flow opened print confirm)
  useEffect(() => {
    if (wasModalOpenRef.current && !modalOpen) {
      if (skipCustomerFocusOnceRef.current) {
        skipCustomerFocusOnceRef.current = false;
        return;
      }
      requestAnimationFrame(() => {
        customerSelectRef.current?.focus?.();
      });
    }
    wasModalOpenRef.current = modalOpen;
  }, [modalOpen]);

  const refreshAll = async () => {
    try {
      await Promise.allSettled([refetchCustomers?.(), refetchTxns?.()]);
      toast.success("Refreshed");
    } catch {}
  };

  const openAdd = () => {
    if (!customerId) return toast.error("Select customer");
    setModalAction("ADD");
    setModalOpen(true);
  };

  const openWithdraw = () => {
    if (!customerId) return toast.error("Select customer");
    setModalAction("WITHDRAW");
    setModalOpen(true);
  };

  const walletBalance = pickWalletBalance(selectedCustomer);

  const walletBadge = useMemo(() => {
    if (!selectedCustomer) return null;
    return (
      <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50/70 px-3 py-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="min-w-0">
            <div className="truncate text-[12px] font-semibold text-slate-800">
              {selectedCustomer?.customerName || "Customer"}{" "}
              <span className="text-slate-500 font-semibold">
                · {selectedCustomer?.customerId || ""}
              </span>
            </div>

            <div className="mt-0.5 text-[10px] text-slate-600">
              Wallet Balance:{" "}
              <span className="font-semibold">
                {walletBalance == null ? "-" : fmtINR(walletBalance)}
              </span>
            </div>
          </div>

          <span className="inline-flex items-center rounded-lg border bg-white px-2 py-1 text-[10px] font-semibold text-slate-600">
            Wallet
          </span>
        </div>
      </div>
    );
  }, [selectedCustomer, walletBalance]);

  const showListSkeleton =
    !!customerId && txnLoading && walletRows.length === 0;

  const isReversibleRow = (r) => {
    if (!r) return false;
    if (isReversalRow(r)) return false;
    if (
      r?.isReversed ||
      r?.reversed ||
      String(r?.status || "").toUpperCase() === "REVERSED"
    )
      return false;
    return !!pickTxLedgerId(r);
  };

  const openReverse = (r, title, amountLabel) => {
    const ledgerId = pickTxLedgerId(r);
    if (!ledgerId) return toast.error("LedgerId not found.");
    setReverseTarget({ ledgerId, title, amountLabel });
    setReverseOpen(true);
  };

  const handleReceiptPrint = async (ledgerId) => {
    if (!ledgerId) return toast.error("LedgerId not found");

    try {
      setReceiptPreview({ open: true, html: "", loading: true });

      const res = await fetch(
        `${process.env.REACT_APP_API_BASE_URL || ""}/customers/transactions/${ledgerId}/receipt-print`,
        {
          method: "GET",
          credentials: "include",
        },
      );

      const html = await res.text();

      if (!res.ok) throw new Error(html || "Failed to load receipt");

      setReceiptPreview({ open: true, html, loading: false });
    } catch (err) {
      console.error(err);
      toast.error("Failed to load receipt preview");
      setReceiptPreview({ open: false, html: "", loading: false });
    }
  };

  const closeReceiptPreview = () => {
    setReceiptPreview({ open: false, html: "", loading: false });
  };

  const confirmReverse = async () => {
    try {
      const ledgerId = reverseTarget?.ledgerId;
      if (!ledgerId) return toast.error("LedgerId missing");

      await reverseTx({
        ledgerId,
        reason: "USER_REVERSE",
        note: "Reversed from GeneralPayments UI",
      }).unwrap();

      toast.success("Transaction reversed");
      setReverseOpen(false);
      setReverseTarget(null);

      refetchTxns?.();
      refetchCustomers?.();
    } catch (e) {
      const msg =
        e?.data?.message || e?.data?.msg || e?.error || "Reverse failed";
      toast.error(msg);
    }
  };

  // ✅ called from WalletPayWithdrawModal when it wants to close payment modal
  // but print confirm is going to open (so don't focus customer yet)
  const closeForPrintFlow = useCallback(() => {
    skipCustomerFocusOnceRef.current = true;
    setModalOpen(false);
  }, []);

  // ✅ called when print confirm closes (Cancel/Print) => focus customer
  const focusCustomerAfterPrintFlow = useCallback(() => {
    requestAnimationFrame(() => {
      customerSelectRef.current?.focus?.();
    });
  }, []);

  return (
    <div className="w-full pb-3 md:h-[calc(100dvh-112px)] md:min-h-0 md:pb-0">
      <div className="grid grid-cols-1 items-stretch gap-3 md:h-full md:min-h-0 lg:grid-cols-[400px_minmax(0,1fr)] lg:gap-4">
        {/* Left side */}
        <motion.div
          variants={fadeUp(0)}
          initial="hidden"
          animate="visible"
          className="overflow-visible rounded-xl border bg-white shadow-sm md:flex md:h-full md:min-h-0 md:flex-col md:overflow-hidden"
        >
          <div className="flex items-center justify-between bg-[#9481ce] px-3 py-2.5 text-white sm:px-4 sm:py-3">
            <div className="flex items-center gap-2 min-w-0">
              <span className="inline-flex h-9 w-9 items-center justify-center rounded-2xl bg-white/10 border border-white/15">
                <FaWallet className="text-white" />
              </span>
              <div className="min-w-0">
                <h1 className="text-sm font-semibold tracking-tight sm:text-base">
                  General Payments
                </h1>
                <p className="text-[11px] text-violet-100/90 truncate">
                  Add to Wallet & Withdraw
                </p>
              </div>
            </div>

            <button
              onClick={refreshAll}
              className="group inline-flex items-center gap-2 rounded-full bg-white text-[#6b4fd8] px-3.5 py-2 text-[11px] font-semibold shadow-sm hover:bg-violet-50 transition"
              title="Refresh"
            >
              <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-[#6b59c6]/10">
                <FaRedoAlt className="text-[12px] text-[#6b59c6]" />
              </span>
              <span className="hidden sm:inline">Refresh</span>
            </button>
          </div>

          <div className="bg-white p-3 sm:p-4 md:min-h-0 md:flex-1 md:overflow-auto">
            <div className="grid grid-cols-1 gap-2.5 sm:gap-3">
              <div>
                <label className="block text-[11px] font-semibold text-slate-600 mb-1">
                  Customer <span className="text-rose-500">*</span>
                </label>
                <Select
                  ref={customerSelectRef}
                  value={customerOpt}
                  onChange={(v) => setCustomerOpt(v)}
                  options={customerOptions}
                  styles={selectStyles}
                  menuPortalTarget={menuPortalTarget}
                  placeholder={custLoading ? "Loading..." : "Select customer"}
                  isClearable
                  menuPlacement="auto"
                />
              </div>

              <div className="flex items-end justify-start gap-2">
                <motion.button
                  whileHover={{ scale: 1.02, y: -1 }}
                  whileTap={{ scale: 0.98, y: 0 }}
                  type="button"
                  onClick={openAdd}
                  className="flex-1 inline-flex items-center justify-center gap-2 rounded-2xl sm:rounded-full bg-[#6b59c6] px-4 py-2.5 text-[11px] font-semibold text-white shadow-sm hover:opacity-95 transition-all"
                >
                  <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-white/15">
                    <FaWallet className="text-[11px]" />
                  </span>
                  Add to Wallet
                </motion.button>

                <motion.button
                  whileHover={{ scale: 1.02, y: -1 }}
                  whileTap={{ scale: 0.98, y: 0 }}
                  type="button"
                  onClick={openWithdraw}
                  className="flex-1 inline-flex items-center justify-center gap-2 rounded-2xl sm:rounded-full bg-white text-[#6b59c6] px-4 py-2.5 text-[11px] font-semibold border border-violet-200 shadow-sm hover:bg-violet-50 transition-all"
                >
                  <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-[#6b59c6]/10">
                    <FaWallet className="text-[11px] text-[#6b59c6]" />
                  </span>
                  Withdraw
                </motion.button>
              </div>
            </div>

            {walletBadge}
          </div>
        </motion.div>

        {/* Right side */}
        <motion.div
          variants={fadeUp(0.1)}
          initial="hidden"
          animate="visible"
          className="min-w-0 overflow-hidden rounded-xl border bg-white shadow-sm md:flex md:min-h-0 md:flex-col"
        >
          <div className="shrink-0 border-b bg-gradient-to-b from-slate-50 to-white px-3 py-2.5 sm:px-4 sm:py-3">
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                  <span>Wallet Transactions</span>
                  <span className="text-[11px] text-slate-500 font-semibold">
                    {txnLoading ? "" : `(${walletRows.length})`}
                  </span>
                </div>
                <div className="text-[11px] text-slate-500 mt-0.5">
                  {customerId
                    ? "Selected customer wallet entries"
                    : "Select customer first"}
                </div>
              </div>

              <div className="flex items-center gap-2">
                <ListLoadingPill show={!!customerId && txnLoading} />
              </div>
            </div>
          </div>

          <div className="relative p-3 sm:p-4 md:min-h-0 md:flex-1 md:overflow-hidden">
            <ListShimmerOverlay show={showListSkeleton} />

            {!customerId ? (
              <EmptyState
                icon={<FaUserCircle className="text-[22px]" />}
                title="Select customer"
                subtitle="Customer choose karo, wallet transactions dikh jayenge."
              />
            ) : showListSkeleton ? (
              <div className="space-y-2">
                {Array.from({ length: 8 }).map((_, i) => (
                  <SkeletonTxRow key={i} />
                ))}
              </div>
            ) : !walletRows.length ? (
              <EmptyState
                icon={<FaWallet className="text-[22px]" />}
                title="No wallet transactions"
                subtitle="Is customer ke wallet me koi entry nahi mili."
              />
            ) : (
              <div className="max-h-[58dvh] space-y-2 overflow-auto pr-1 md:h-full md:max-h-none">
                {walletRows.map((r, idx) => {
                  const action = getWalletAction(r);
                  const isWithdraw = action === "WITHDRAW";

                  const reversedOriginal = !!r?.isReversed;
                  const reversalRow = isReversalRow(r);

                  const amtCredit = num(r?.credit);
                  const amtDebit = num(r?.debit);
                  const rawAmount = r?.amount != null ? num(r.amount) : 0;
                  const amount = rawAmount
                    ? rawAmount
                    : isWithdraw
                      ? amtDebit
                      : amtCredit;

                  const title =
                    r?.itemsSummary ||
                    r?.title ||
                    (reversalRow
                      ? r?.note || "Reversal entry"
                      : isWithdraw
                        ? "Wallet withdraw"
                        : "Added to wallet");

                  const ledgerIdForPrint = pickTxLedgerId(r);
                  const canPrint = !!(ledgerIdForPrint || r?.printUrl);

                  const badgeText = reversalRow
                    ? "REVERSAL"
                    : reversedOriginal
                      ? "REVERSED"
                      : isWithdraw
                        ? "WITHDRAW"
                        : "ADDED";

                  const badgeClass = reversalRow
                    ? "bg-indigo-50 text-indigo-700 border-indigo-200"
                    : reversedOriginal
                      ? "bg-slate-100 text-slate-600 border-slate-200"
                      : isWithdraw
                        ? "bg-amber-50 text-amber-700 border-amber-200"
                        : "bg-emerald-50 text-emerald-700 border-emerald-200";

                  return (
                    <motion.div
                      key={r?._id || `${idx}`}
                      custom={Math.min(idx, 10)}
                      variants={listVariants}
                      initial="hidden"
                      animate="visible"
                      className="rounded-2xl border bg-white px-3 py-2.5 shadow-sm hover:shadow-md transition-all"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <div
                              className={`inline-flex items-center rounded-full px-2.5 py-1 text-[10px] font-extrabold border ${badgeClass}`}
                            >
                              {badgeText}
                            </div>

                            <div className="text-[12px] font-extrabold text-slate-800 truncate">
                              {title}
                            </div>
                          </div>

                          <div className="text-[10px] text-slate-500 mt-1">
                            {toYmd(r?.date)} ·{" "}
                            {r?.paymentMode || r?.mode || "-"}
                            {r?.txnNo ? ` · ${r.txnNo}` : ""}
                            {r?.displaySourceType
                              ? ` · ${r.displaySourceType}`
                              : ""}
                          </div>

                          {r?.note && !reversalRow ? (
                            <div className="text-[10px] text-slate-500 mt-1 line-clamp-2">
                              {r.note}
                            </div>
                          ) : null}
                        </div>

                        <div className="text-right shrink-0">
                          <div
                            className={`text-[15px] sm:text-base font-extrabold ${
                              reversedOriginal
                                ? "text-slate-400 line-through"
                                : "text-slate-900"
                            }`}
                          >
                            {fmtINR(amount)}
                          </div>

                          <div className="text-[10px] text-slate-500">
                            {amtDebit > 0 ? "Debit" : "Credit"}
                          </div>

                          <div className="mt-1.5 flex items-center justify-end gap-2">
                            <button
                              type="button"
                              disabled={!canPrint}
                              onClick={() => {
                                handleReceiptPrint(ledgerIdForPrint);
                              }}
                              className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[10px] font-semibold ${
                                canPrint
                                  ? "border-violet-200 bg-white text-[#6b59c6] hover:bg-violet-50"
                                  : "border-slate-200 bg-slate-100 text-slate-400 cursor-not-allowed"
                              }`}
                              title={
                                canPrint ? "Print slip" : "Slip not available"
                              }
                            >
                              <FiPrinter className="text-[12px]" />
                              Slip
                            </button>

                            <button
                              type="button"
                              disabled={
                                !isReversibleRow(r) || reverseState.isLoading
                              }
                              onClick={() =>
                                openReverse(r, title, fmtINR(amount))
                              }
                              className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[10px] font-semibold ${
                                !isReversibleRow(r) || reverseState.isLoading
                                  ? "border-slate-200 bg-slate-100 text-slate-400 cursor-not-allowed"
                                  : "border-rose-200 bg-white text-rose-700 hover:bg-rose-50"
                              }`}
                            >
                              <FiRotateCcw className="text-[12px]" />
                              Reverse
                            </button>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            )}
          </div>
        </motion.div>
      </div>

      <WalletPayWithdrawModal
        onPrintReceipt={(ledgerId) => {
          handleReceiptPrint(ledgerId);
        }}
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        customerId={customerId}
        customerLabel={customerOpt?.label || ""}
        branchId={
          selectedCustomer?.branchId || selectedCustomer?.branch?._id || null
        }
        walletBalance={walletBalance}
        action={modalAction}
        // ✅ existing confirm modal reuse: success ke baad modal itself will open its print-confirm,
        // and call below to close add/withdraw UI without stealing focus
        onCloseForPrintFlow={closeForPrintFlow}
        onPrintFlowDone={focusCustomerAfterPrintFlow}
        onSuccess={() => {
          refetchTxns?.();
          refetchCustomers?.();
        }}
      />

      {/* Reverse confirm modal */}
      <AnimatePresence>
        {reverseOpen && (
          <motion.div
            className="fixed inset-0 z-[10010] bg-black/45 backdrop-blur-sm flex items-center justify-center p-3"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onMouseDown={(e) => {
              if (e.target === e.currentTarget && !reverseState.isLoading) {
                setReverseOpen(false);
                setReverseTarget(null);
              }
            }}
          >
            <motion.div
              initial={{ opacity: 0, y: 16, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 16, scale: 0.98 }}
              className="w-full max-w-md rounded-3xl bg-white shadow-2xl border border-slate-200 overflow-hidden"
            >
              <div className="px-4 py-3 border-b bg-gradient-to-r from-rose-600 via-rose-500 to-amber-500 text-white">
                <div className="text-[13px] font-extrabold">
                  Reverse transaction?
                </div>
                <div className="text-[11px] text-white/90 mt-0.5">
                  This will create a reverse entry and update wallet balance.
                </div>
              </div>

              <div className="p-4">
                <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-3 text-[11px]">
                  <div className="text-slate-700">
                    <span className="font-semibold">Title:</span>{" "}
                    {reverseTarget?.title || "-"}
                  </div>
                  <div className="text-slate-700 mt-1">
                    <span className="font-semibold">Amount:</span>{" "}
                    {reverseTarget?.amountLabel || "-"}
                  </div>
                  <div className="text-slate-500 mt-1">
                    LedgerId: {reverseTarget?.ledgerId || "-"}
                  </div>
                </div>

                <div className="mt-3 flex flex-col sm:flex-row gap-2 justify-end">
                  <button
                    type="button"
                    disabled={reverseState.isLoading}
                    onClick={() => {
                      setReverseOpen(false);
                      setReverseTarget(null);
                    }}
                    className="w-full sm:w-auto inline-flex items-center justify-center rounded-2xl sm:rounded-full border border-slate-300 bg-slate-50 px-4 py-2 text-[11px] font-semibold text-slate-700 hover:bg-slate-100 disabled:opacity-60"
                  >
                    Cancel
                  </button>

                  <button
                    type="button"
                    disabled={reverseState.isLoading}
                    onClick={confirmReverse}
                    className="w-full sm:w-auto inline-flex items-center justify-center rounded-2xl sm:rounded-full bg-rose-600 px-4 py-2 text-[11px] font-semibold text-white hover:bg-rose-700 disabled:opacity-60"
                  >
                    {reverseState.isLoading ? "Reversing..." : "Yes, Reverse"}
                  </button>
                </div>

                <div className="mt-2 text-[10px] text-slate-500">
                  Reverse ke baad list & wallet balance refresh ho jayega.
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <InvoicePrintPreviewModal
        open={receiptPreview.open}
        html={receiptPreview.html}
        loading={receiptPreview.loading}
        onClose={closeReceiptPreview}
      />
    </div>
  );
}

function EmptyState({ icon, title, subtitle }) {
  return (
    <div className="rounded-2xl border bg-slate-50 p-4 text-center">
      <div className="mx-auto mb-2 inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-white border shadow-sm text-slate-700">
        {icon}
      </div>
      <div className="text-sm font-extrabold text-slate-800">{title}</div>
      <div className="text-[11px] text-slate-500 mt-1">{subtitle}</div>
    </div>
  );
}
